// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import {
  BillingPlanStatus,
  CurrencyPlanStatusSummary,
  PlanStatusSummary,
} from '@/billing/api/types';
import { PLAN_CARD_COLOR_TOKENS } from '@/billing/components/billing-plan-status-constants';
import { PerCurrencySections } from '@/billing/components/currency-section-accordion';
import { PrimaryMetric, SummaryCard } from '@/components/summary-card';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { pluralize } from '@/utilities/pluralize';
import { Box, Grid } from '@mui/material';

interface Props {
  // Null while the listing is loading. The component renders skeleton
  // metrics in that state instead of fabricating zeros.
  summary: PlanStatusSummary | null;
}

// Fixed tile order so the skeleton layout matches the loaded layout. Per-bucket
// tooltips are brief glossary entries that define the term, not the count.
const PLAN_TILES: {
  status: BillingPlanStatus;
  label: string;
  tooltip: string;
  amount: (bucket: CurrencyPlanStatusSummary) => string;
  count: (bucket: CurrencyPlanStatusSummary) => number;
}[] = [
  {
    status: 'PENDING',
    label: 'Pending',
    tooltip: 'Plan is awaiting an activation date',
    amount: (bucket) => bucket.pendingAmount,
    count: (bucket) => bucket.pendingCount,
  },
  {
    status: 'CONFIRMED',
    label: 'Confirmed',
    tooltip: 'Plan is scheduled but no charges have been billed',
    amount: (bucket) => bucket.confirmedAmount,
    count: (bucket) => bucket.confirmedCount,
  },
  {
    status: 'ACTIVE',
    label: 'Active',
    tooltip: 'At least one charge is billable or on an invoice',
    amount: (bucket) => bucket.activeAmount,
    count: (bucket) => bucket.activeCount,
  },
  {
    status: 'COMPLETE',
    label: 'Complete (last 30 days)',
    tooltip: 'Plan was completed in the last 30 days',
    amount: (bucket) => bucket.completeAmount,
    count: (bucket) => bucket.completeCount,
  },
];

// Four-card row above the listing showing org-wide plan totals
// (Pending / Confirmed / Active / Complete), bucketed per contract-value
// currency. Totals reflect the org-wide dataset regardless of active filters so
// the buckets stay readable as the user filters the table.
export function BillingPlanSummaryCards(props: Props) {
  const { summary } = props;

  if (summary === null) {
    return (
      <Box sx={{ mb: 2 }}>
        <CurrencyTileRow bucket={null} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <PerCurrencySections
        buckets={summary.byCurrency}
        renderBucket={(bucket) => <CurrencyTileRow bucket={bucket} />}
      />
    </Box>
  );
}

interface CurrencyTileRowProps {
  // `null` signals the loading state; every tile renders a skeleton.
  bucket: CurrencyPlanStatusSummary | null;
}

function CurrencyTileRow(props: CurrencyTileRowProps) {
  const { bucket } = props;
  const isLoading = bucket === null;

  return (
    <Grid container spacing={3}>
      {PLAN_TILES.map((tile) => (
        <Grid item xs={12} sm={6} md={3} key={tile.status}>
          <SummaryCard>
            <PrimaryMetric
              label={tile.label}
              value={
                bucket
                  ? formatAmountWithCurrency(
                      tile.amount(bucket),
                      bucket.isoCode
                    )
                  : ''
              }
              color={PLAN_CARD_COLOR_TOKENS[tile.status]}
              isLoading={isLoading}
              tooltip={tile.tooltip}
              caption={
                bucket
                  ? pluralize(tile.count(bucket), { singular: 'plan' })
                  : undefined
              }
            />
          </SummaryCard>
        </Grid>
      ))}
    </Grid>
  );
}
